/**
 * Session Title Utility
 *
 * Generates smart, descriptive titles for sessions based on conversation content.
 * Titles are derived from the first user message to provide meaningful context.
 *
 * Supports AI-powered title generation via the daemon's Claude SDK,
 * using the user's existing Claude Code CLI Max subscription.
 */

import { requestTitleGeneration } from '../adapters';

const MAX_TITLE_LENGTH = 40;

/**
 * Generate an AI-powered session title using the daemon's Claude SDK
 *
 * Uses socket events to communicate with the daemon, which has access to
 * the user's Claude Code CLI Max subscription via the Anthropic SDK.
 *
 * @param message - The user message to generate a title from
 * @param sessionId - Session ID to associate with the title
 * @returns Promise resolving to the generated title
 *
 * Falls back to local generation if daemon is not connected or fails.
 */
export async function generateAiSessionTitle(
  message: string,
  sessionId?: string
): Promise<{ title: string; aiGenerated: boolean }> {
  // If no sessionId provided, can't use socket-based generation
  if (!sessionId) {
    console.warn('[sessionTitle] No sessionId provided, using local fallback');
    return { title: generateSessionTitle(message), aiGenerated: false };
  }

  try {
    // Try socket-based generation via daemon (uses Claude subscription)
    const result = await requestTitleGeneration(message, sessionId);

    if (result) {
      console.log(`[sessionTitle] AI title generated via daemon: "${result.title}" (aiGenerated: ${result.aiGenerated})`);
      return result;
    }

    // Daemon not connected or failed
    console.warn('[sessionTitle] Daemon title generation unavailable, using local fallback');
    return { title: generateSessionTitle(message), aiGenerated: false };
  } catch (error) {
    console.warn('[sessionTitle] AI title generation failed, using fallback:', error);
    return { title: generateSessionTitle(message), aiGenerated: false };
  }
}

/**
 * Generate a smart session title from the first user message content
 *
 * @param firstMessage - The content of the first user message
 * @returns A descriptive title for the session
 *
 * @example
 * ```typescript
 * generateSessionTitle('/commit')
 * // Returns: 'New Session' (slash commands only are excluded)
 *
 * generateSessionTitle('Add a dark mode toggle to settings')
 * // Returns: 'Add a dark mode toggle to settings'
 *
 * generateSessionTitle('I need help implementing user authentication with OAuth2 and JWT tokens')
 * // Returns: 'Help implementing user auth...'
 * ```
 */
export function generateSessionTitle(firstMessage: string): string {
  if (!firstMessage || !firstMessage.trim()) {
    return 'New Session';
  }

  // Clean the message
  let clean = firstMessage
    .trim()
    // Remove leading slash commands (e.g., "/commit ", "/deploy ")
    .replace(/^\/\S+\s*/g, '')
    // Remove markdown headers
    .replace(/^#+\s*/g, '')
    // Remove leading/trailing whitespace
    .trim();

  // If nothing left after cleaning (e.g., just "/commit"), return default
  if (!clean) {
    return 'New Session';
  }

  // Take the first sentence or first N characters
  const firstSentenceEnd = clean.search(/[.!?]\s/);
  if (firstSentenceEnd > 0 && firstSentenceEnd < MAX_TITLE_LENGTH) {
    clean = clean.slice(0, firstSentenceEnd + 1);
  }

  // Capitalize first letter
  clean = clean.charAt(0).toUpperCase() + clean.slice(1);

  // Truncate if too long
  if (clean.length > MAX_TITLE_LENGTH) {
    // Try to break at a word boundary
    const truncated = clean.slice(0, MAX_TITLE_LENGTH);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > MAX_TITLE_LENGTH * 0.6) {
      // Break at word boundary if reasonable
      return truncated.slice(0, lastSpace) + '...';
    }
    return truncated + '...';
  }

  return clean;
}

/**
 * Format a session ID for display (shorter, more readable)
 *
 * @param sessionId - The full session ID (e.g., "sdk-session-d482428f952e")
 * @returns A shortened display version
 *
 * @example
 * ```typescript
 * formatSessionId('sdk-session-d482428f952e')
 * // Returns: 'd482428'
 *
 * formatSessionId('session-1234567890abcdef')
 * // Returns: '1234567'
 * ```
 */
/**
 * Common stop words to exclude from topic analysis
 */
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'what', 'how', 'can', 'you', 'me', 'my', 'this', 'that', 'it', 'its',
  'to', 'for', 'in', 'on', 'with', 'and', 'or', 'but', 'not', 'of',
  'at', 'by', 'from', 'up', 'about', 'into', 'over', 'after',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'get', 'got', 'make', 'made', 'just', 'only', 'also', 'like', 'need', 'want',
  'please', 'help', 'thanks', 'thank', 'new', 'now', 'some', 'all', 'any',
]);

/**
 * Extract key topic words from a message
 *
 * @param message - The message content to analyze
 * @returns Array of significant keywords (up to 5)
 *
 * @example
 * ```typescript
 * extractTopicKeywords('Add dark mode toggle to settings')
 * // Returns: ['add', 'dark', 'mode', 'toggle', 'settings']
 *
 * extractTopicKeywords('/commit fix the bug')
 * // Returns: ['fix', 'bug']
 * ```
 */
export function extractTopicKeywords(message: string): string[] {
  const cleaned = message
    .replace(/^\/\S+\s*/, '') // Remove slash commands
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Remove punctuation
    .trim();

  // Get significant words (skip common words and short words)
  const words = cleaned
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w));

  return words.slice(0, 5); // Top 5 keywords
}

/**
 * Check if the topic has changed significantly between the current title and a new message
 *
 * @param currentTitle - The current session title
 * @param newMessage - The new message to compare against
 * @returns true if topic has changed significantly (less than 20% keyword overlap)
 *
 * @example
 * ```typescript
 * hasTopicChanged('Add dark mode toggle', 'Update the dark mode colors')
 * // Returns: false (overlapping keywords: 'dark', 'mode')
 *
 * hasTopicChanged('Add dark mode toggle', 'Fix authentication bug')
 * // Returns: true (no overlapping keywords)
 * ```
 */
export function hasTopicChanged(currentTitle: string, newMessage: string): boolean {
  const titleKeywords = extractTopicKeywords(currentTitle);
  const messageKeywords = extractTopicKeywords(newMessage);

  // If either has no keywords, can't determine change
  if (titleKeywords.length === 0 || messageKeywords.length === 0) {
    return false;
  }

  // Count overlapping keywords
  const overlap = titleKeywords.filter((k) => messageKeywords.includes(k)).length;

  // If less than 20% overlap, topic has changed
  const threshold = Math.min(titleKeywords.length, messageKeywords.length) * 0.2;
  return overlap < threshold;
}

export function formatSessionId(sessionId: string): string {
  if (!sessionId) return '';

  // Remove common prefixes
  let short = sessionId
    .replace(/^sdk-session-/i, '')
    .replace(/^session-/i, '')
    .replace(/^sess-/i, '');

  // Take first 7 characters (like git short hash)
  return short.slice(0, 7);
}

/**
 * Get display title for a session
 * Uses smart title if available, falls back to formatted session_name or ID
 *
 * @param session - Session object with optional title
 * @returns The best available title for display
 */
export function getSessionDisplayTitle(session: {
  session_id: string;
  session_name: string;
  title?: string;
}): string {
  // Prefer smart title if set
  if (session.title) {
    return session.title;
  }

  // Handle missing session_name
  if (!session.session_name) {
    return 'New Session';
  }

  // Check if session_name is a raw ID (contains uuid-like patterns)
  const isRawId =
    session.session_name.startsWith('sdk-session-') ||
    session.session_name.startsWith('session-') ||
    /^[a-f0-9-]{8,}$/i.test(session.session_name);

  if (isRawId) {
    return 'New Session';
  }

  return session.session_name;
}
